import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Code2, Eye, X } from "lucide-react";

const IMG = /\.(png|jpe?g|gif|webp|svg)$/i;
const HTML = /\.html?$/i;

/** Views a /workspace file: HTML rendered, markdown rendered, images shown, else text. */
export function FileViewer({ path, onClose }: { path: string | null; onClose: () => void }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [source, setSource] = useState(false);

  const isImg = !!path && IMG.test(path);
  const isHtml = !!path && HTML.test(path);
  const needsText = !!path && !isImg && (!isHtml || source);

  useEffect(() => {
    setSource(false);
  }, [path]);

  useEffect(() => {
    if (!path || !needsText) return;
    setText("");
    setErr(null);
    fetch(`/api/file?path=${encodeURIComponent(path)}`)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then(setText)
      .catch(() => setErr("Couldn't load this file."));
  }, [path, needsText]);

  if (!path) return null;
  const name = path.split("/").pop() || path;
  const src = `/api/file?path=${encodeURIComponent(path)}`;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal file-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span title={path}>{name}</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {isHtml && (
              <button className="ghost btn-icon" onClick={() => setSource((s) => !s)}>
                {source ? <Eye size={14} /> : <Code2 size={14} />}
                {source ? "Preview" : "Source"}
              </button>
            )}
            <button className="ghost icon" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="file-body">
          {isImg ? (
            <img className="file-img" src={src} alt={name} />
          ) : isHtml && !source ? (
            <iframe
              className="file-frame"
              title={name}
              src={src}
              sandbox="allow-scripts allow-popups allow-forms allow-modals"
            />
          ) : err ? (
            <p className="error">{err}</p>
          ) : /\.md$/i.test(path) ? (
            <div className="md">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            </div>
          ) : (
            <pre className="file-pre">{text}</pre>
          )}
        </div>
      </div>
    </div>
  );
}
